import { computed, observable, action } from 'mobx'
import { task } from 'mobx-task'
import { chunk } from 'lodash-es'
import {
  catchError,
  combineLatest,
  defer,
  EMPTY,
  firstValueFrom,
  filter,
  map,
  switchMap,
} from 'rxjs'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { NewUserTransactionDialogViewModel } from './NewUserTransactionDialogViewModel'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type GetCurrentUserFn } from '@/features/iam'
import { PendingTransactionViewModel } from './PendingTransactionViewModel'
import { CancelUserTransactionDialogViewModel } from './CancelUserTransactionDialogViewModel'
import { type PendingTransaction } from '@/features/payouts/admin/PendingTransaction'
import {
  type PayoutReviewStore,
  type ListPendingTransactionsQuery,
} from '@/features/payouts/admin/PayoutReviewStore'
import { PayoutDraftingViewModel } from './PayoutDraftingViewModel'
import { tapAction } from '@/utils/mobx-util'
import { csvDownload } from '@/utils/csv-util'
import { type DateRange, DateRangeFilter } from '@/infra/DateRangeFilter'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { FiltersViewModel, FilterItemSource } from '@/infra/FiltersViewModel'
import { SelectFilter } from '@/infra/SelectFilter'
import { mapPendingTransactionsToCsv } from '@/features/payouts/admin/payout-csv-mappers'
import type { TransactionFlag } from '@/features/payouts/transaction-flag'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import type { Id } from '@/infra/id'
import type { TransactionSourceType } from '@/features/payouts/transaction-source-type'
import { InfiniteSelectFilter } from '@/infra/InfiniteSelectFilter'
import type { SelectionState } from '@/infra/selection-state'

/**
 * Filter for historical transactions.
 */
export interface PendingTransactionFilter {
  workspaces: Id[]
  createdRange: DateRange
  transactedRange: DateRange
  flags: TransactionFlag[]
  sources: TransactionSourceType[]
}

/**
 * Pending transactions query, without paging.
 */
type PendingTransactionsQuery = Omit<
  ListPendingTransactionsQuery,
  keyof CursorPageQuery
>

/**
 * View model for managing the overall payout review screen.
 */
export class PayoutReviewViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Pending transaction view models.
   */
  private readonly _pendingTransactionVms

  /**
   * The selected transactions.
   */
  private readonly _selected = observable.set<PendingTransaction>()

  /**
   * Manually created minus cancelled transactions, to keep a somewhat
   * accurate count of the total pending transactions.
   */
  @observable private accessor _manualDelta = 0

  /**
   * The query to use.
   */
  private readonly _query$ = defer(() =>
    combineLatest([
      this.searchText.debouncedValue$,
      this.filters.appliedFilters$,
    ]),
  ).pipe(
    map(([searchText, filters]): PendingTransactionsQuery => ({
      searchText,
      dateCreated: filters.createdRange,
      dateTransacted: filters.transactedRange,
      flags: filters.flags,
      sources: filters.sources,
      workspaceIds: filters.workspaces,
    })),
    shareLatest(),
  )

  /**
   * Fetch pipeline.
   */
  private readonly _fetch$ = this._query$.pipe(
    filter(() => this.canAccessPayoutsAdministration),
    switchMap((query) => {
      // Clear the selection when changing the filter.
      this.clearSelection()
      return withAbortSignal((signal) =>
        this.pendingTransactionsScroller.fetch({ filter: query, signal }),
      ).pipe(
        // The retry alert has reported the failure.
        catchError(() => EMPTY),
      )
    }),
  )

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Filters.
   */
  readonly filters = new FiltersViewModel<PendingTransactionFilter>({
    definitions: {
      createdRange: {
        icon: 'date-created',
        title: 'Date Created',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      transactedRange: {
        icon: 'date-transacted',
        title: 'Date Transacted',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      flags: {
        icon: 'flags',
        title: 'Flags',
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static([
              {
                title: 'Job canceled',
                value: 'JobCanceled',
              },
              {
                title: 'Split auto-adjusted',
                value: 'JobPayoutAutoAdjusted',
              },
            ]),
            true,
          ),
      },
      sources: {
        icon: 'transaction-source',
        title: 'Transaction source',
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static([
              {
                title: 'User transactions',
                value: 'User',
              },
              {
                title: 'Job transactions',
                value: 'Job',
              },
            ]),
            true,
          ),
      },
      workspaces: {
        icon: 'workspace-name',
        title: 'Workspaces',
        persist: true,
        factory: (key, title) => {
          return new InfiniteSelectFilter(
            key,
            title,
            ({ paging, filter, signal }) =>
              this._listPlatformWorkspaces(paging, filter, signal),
            true,
            true,
            3,
          )
        },
      },
    },
  })

  /**
   * The view model for cancelling a user transaction.
   */
  readonly cancelUserTransactionVm

  /**
   * Scroller for the pending transactions.
   */
  readonly pendingTransactionsScroller = new InfiniteScrollProvider<
    PendingTransaction,
    PendingTransactionsQuery
  >({
    limit: 200,
    fetchItems: ({ paging, signal, filter = {} }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Unable to load pending transactions',
        signal,
      )

      return withRetry(() =>
        this._payoutReviewStore.listPendingTransactions(
          { ...filter, ...paging },
          signal,
        ),
      )
    },
  })

  /**
   * The view model for creating a new user transaction.
   */
  readonly newUserTransactionVm

  /**
   * The view model for drafting a payout (approve/remove transactions).
   */
  readonly draftingVm

  constructor(
    private readonly _payoutReviewStore: PayoutReviewStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getCurrentUser: GetCurrentUserFn,
  ) {
    this.cancelUserTransactionVm = new CancelUserTransactionDialogViewModel(
      this._payoutReviewStore,
      this._newAlert,
    )

    this.newUserTransactionVm = new NewUserTransactionDialogViewModel(
      this._payoutReviewStore,
      this._newAlert,
    )

    this.draftingVm = new PayoutDraftingViewModel(
      this._payoutReviewStore,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this._pendingTransactionVms = new ViewModelList({
      models: () =>
        // Filter out the ones that have already been approved
        this.pendingTransactionsScroller.items.filter(
          (pendingTransaction) =>
            !this.draftingVm.draft.approvedTransactionIds.has(
              pendingTransaction.id,
            ),
        ),
      create: (model) =>
        new PendingTransactionViewModel(
          model,
          this._selected,
          this.cancelUserTransactionVm.show,
          this.draftingVm.approveTransactions,
        ),
    })

    this._lifecycle.manage(this._fetch$)
    this._lifecycle.manage(this.filters)
    this._lifecycle.manage(this._pendingTransactionVms)
    this._lifecycle.manage(this.draftingVm)
    this._lifecycle.manage(this.newUserTransactionVm)
    this._lifecycle.manage(
      syncInfiniteScrollerItems({
        scroller: this.pendingTransactionsScroller,
        additions$: this._payoutReviewStore.pendingTransactionAdded$,
        removals$: this._payoutReviewStore.pendingTransactionRemoved$,
        insertAt: 'start',
      }).pipe(
        tapAction((change) => {
          this._manualDelta += change.type === 'add' ? 1 : -1
        }),
      ),
    )

    // When payout completes, re-fetch the pending transactions
    this._lifecycle.manage(
      this.draftingVm.payoutCompleted$.pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            this.pendingTransactionsScroller.fetch({ signal }),
          ).pipe(catchError(() => EMPTY)),
        ),
      ),
    )
  }

  /**
   * The pending visible transactions.
   */
  @computed
  get pendingTransactions() {
    return this._pendingTransactionVms.items
  }

  /**
   * Whether any filters have been applied, including search text.
   */
  @computed
  get filtersApplied() {
    return !!this.searchText.value || this.filters.appliedFilterCount > 0
  }

  /**
   * How many transactions are pending.
   */
  @computed
  get pendingTransactionsCount() {
    return this.pendingTransactionsScroller.total + this._manualDelta
  }

  /**
   * Whether the pending transactions table shows, rather than the empty state.
   */
  @computed
  get showTable() {
    // The draft filters out approved transactions, so wait for both to avoid
    // flashing the empty state.
    const isLoading =
      this.pendingTransactionsScroller.fetch.pending ||
      this.draftingVm.fetch.pending
    const isEmpty =
      this.pendingTransactions.length === 0 &&
      this.pendingTransactionsScroller.isDone
    return isLoading || !isEmpty
  }

  /**
   * Whether transactions are being approved.
   */
  @computed
  get isApprovePending() {
    return (
      this.approveSelectedTransactions.pending ||
      this.approveTransactionsMatchingFilter.pending
    )
  }

  /**
   * Whether approving every transaction matching the filter is disabled.
   */
  @computed
  get approveMatchingFilterDisabled() {
    return this.pendingTransactions.length === 0 || this.selected.size > 0
  }

  /**
   * Whether actions are disabled.
   */
  @computed
  get actionsDisabled() {
    return this.draftingVm.submit.pending
  }

  /**
   * Returns the selection, filtering out transactions that are already approved.
   */
  @computed
  get selected(): ReadonlySet<PendingTransaction> {
    const result = new Set<PendingTransaction>()
    for (const pendingTransaction of this._selected) {
      if (this._pendingTransactionVms.contains(pendingTransaction)) {
        result.add(pendingTransaction)
      }
    }

    return result
  }

  /**
   * The selection state.
   */
  @computed
  get selectionState(): SelectionState {
    if (this.pendingTransactions.length === 0) {
      return 'NoneSelected'
    }

    const allSelected = this.pendingTransactions.every((transactionVm) =>
      this.selected.has(transactionVm.transaction),
    )

    if (allSelected) {
      return 'AllSelected'
    }

    if (this.selected.size > 0) {
      return 'SomeSelected'
    }

    return 'NoneSelected'
  }

  /**
   * Whether the user can access the payouts administration.
   */
  @computed
  get canAccessPayoutsAdministration() {
    return this._getCurrentUser().platformAdmin
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this.cancelUserTransactionVm.modal.close()
    this._lifecycle.deactivate()
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }

  /**
   * Toggles the selection based on the current list selection.
   */
  @action.bound
  toggleListSelection() {
    switch (this.selectionState) {
      case 'NoneSelected':
      case 'SomeSelected':
        this.pendingTransactions.forEach((transactionVm) =>
          this._selected.add(transactionVm.transaction),
        )
        break
      case 'AllSelected':
        this.clearSelection()
        break
    }
  }

  /**
   * Clears the selected transactions.
   */
  @action.bound
  clearSelection() {
    this._selected.clear()
  }

  /**
   * Pages through all pending transactions and exports all of them as CSV.
   */
  exportCsv = task.resolved(async () => {
    const alert = this._newAlert()
      .withState('pending')
      .saying('Exporting', 'Retrieving data to export')
      .show()
    try {
      const items = await this._enumerateAllMatchingFilter()
      csvDownload(
        mapPendingTransactionsToCsv(items),
        'pending-transactions.csv',
      )
      alert
        .withState('success')
        .saying('Export completed!', 'Your download has started.')
        .autoDismiss()
    } catch (err) {
      alert.dismiss()
      return this._createAlertErrorHandler('Exporting failed')(err)
    }
  })

  /**
   * Approve the selected transactions.
   */
  approveSelectedTransactions = task.resolved(async () => {
    const selected = Array.from(this.selected)
    await this.draftingVm.approveTransactions(selected)
    this.clearSelection()
  })

  /**
   * Pages through all pending transactions and approves them all.
   */
  approveTransactionsMatchingFilter = task.resolved(async () => {
    const alert = this._newAlert()
      .withState('pending')
      .saying(
        'Approving',
        'Approving all transactions matching the current filter...',
      )
      .show()
    let items: PendingTransaction[]
    try {
      // The draft adds every approved transaction it is given, so ones it
      // already holds would be counted twice in its total.
      const matching = await this._enumerateAllMatchingFilter()
      const approvedIds = this.draftingVm.draft.approvedTransactionIds
      items = matching.filter((t) => !approvedIds.has(t.id))
    } catch (err) {
      alert.dismiss()
      return this._createAlertErrorHandler('Approving failed')(err)
    }

    // A failed batch shows its own alert and stops the rest.
    try {
      for (const batch of chunk(items, 10_000)) {
        await this.draftingVm.approveTransactions(batch)
      }
    } finally {
      alert.dismiss()
    }
  })

  /**
   * Queries for all pending transactions matching the current filter.
   * @private
   */
  private async _enumerateAllMatchingFilter() {
    const query = await firstValueFrom(this._query$)
    let cursor: string | null = null
    const items: PendingTransaction[] = []
    do {
      // Annotated, as `page` is otherwise inferred from its own initializer
      // through `cursor`.
      const page: CursorPaged<PendingTransaction> =
        await this._payoutReviewStore.listPendingTransactions({
          ...query,
          limit: 1_000,
          after: cursor,
        })
      items.push(...page.data)
      cursor = page.next
    } while (cursor)
    return items
  }

  /**
   * Lists the platform workspaces.
   */
  private _listPlatformWorkspaces = async (
    page: CursorPageQuery,
    filter?: Record<string, unknown>,
    signal?: AbortSignal,
  ) =>
    this._payoutReviewStore
      .listPayoutWorkspaces(
        {
          ...filter,
          ...page,
        },
        signal,
      )
      .then((r) => CursorPaged.mapT(r, (w) => ({ value: w.id, title: w.name })))
}
